import React from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
} from '@mui/material';

function Section({ title, children }) {
  const theme = useTheme();
  return (
    <Box mb={4}>
      <Typography 
        variant="h5" 
        component="h2" 
        gutterBottom 
        sx={{ 
          color: theme.palette.primary.main,
          fontWeight: 'bold',
          mb: 2 
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function TermsConditions() {
  const theme = useTheme();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2 }}>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          sx={{ 
            color: theme.palette.primary.main,
            fontWeight: 'bold',
            mb: 4 
          }}
        >
          Terms and Conditions
        </Typography>
        
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Last updated: December 29, 2024
        </Typography>

        <Section title="1. Agreement to Terms">
          <Typography paragraph>
            By downloading, installing, or using the Drips Water mobile application ("App"), you agree to be bound by these Terms and Conditions. If you disagree with any part of these terms, you do not have permission to access the App.
          </Typography>
        </Section>

        <Section title="2. About the App">
          <Typography paragraph>
            Drips Water ("we," "our," or "us") is operated by Aadi Manufacturing Private Limited, located at Plot No - 952, At- Pathargadia, Chandaka, Khordha, Odisha, 754005, India.
          </Typography>
        </Section>

        <Section title="3. Definitions">
          <List>
            {[
              { term: 'App', definition: 'The Drips Water mobile application' },
              { term: 'User', definition: 'Any person who accesses or uses the App' },
              { term: 'Content', definition: 'All information, text, graphics, photos, or other materials uploaded, downloaded, or appearing on the App' },
              { term: 'Service', definition: 'All services provided through the App' },
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={`${item.term}:`} secondary={item.definition} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="4. User Account">
          <Typography variant="h6" gutterBottom>4.1 Account Creation</Typography>
          <List>
            {[
              'You may need to create an account to use certain features of the App',
              'You must provide accurate and complete information',
              'You are responsible for maintaining the security of your account'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h6" gutterBottom>4.2 Account Responsibilities</Typography>
          <List>
            {[
              'You are responsible for all activities under your account',
              'You must notify us immediately of any unauthorized use',
              'You must not share your account credentials'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="5. Acceptable Use">
          <Typography variant="h6" gutterBottom>5.1 You agree to:</Typography>
          <List>
            {[
              'Use the App in compliance with all applicable laws',
              'Maintain the confidentiality of your account',
              'Use the App for its intended purpose'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h6" gutterBottom>5.2 You agree not to:</Typography>
          <List>
            {[
              'Modify, adapt, or hack the App',
              'Use the App for any illegal purposes',
              'Transmit viruses or harmful code',
              'Interfere with the App\'s security features',
              'Create multiple accounts for malicious purposes'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h6" gutterBottom>5.3 App Permissions</Typography>
          <Typography variant="subtitle1" gutterBottom>Notification Permission</Typography>
          <List>
            {[
              'You acknowledge that the App requires notification permission to function properly',
              'You have the right to:',
              '- Grant or deny notification permission',
              '- Modify notification settings at any time',
              '- Opt-out of promotional notifications while maintaining essential service notifications',
              'We will:',
              '- Only send relevant and necessary notifications',
              '- Respect your notification preferences',
              '- Provide clear instructions for managing notifications',
              '- Not misuse the notification permission for spam or unnecessary alerts'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="6. Intellectual Property">
          <Typography variant="h6" gutterBottom>6.1 Our Rights</Typography>
          <List>
            {[
              'The App and its original content are the exclusive property of Aadi Manufacturing Private Limited',
              'All related graphics, logos, and service names are trademarks',
              'All rights not expressly granted are reserved'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h6" gutterBottom>6.2 User Content</Typography>
          <List>
            {[
              'You retain rights to content you submit through the App',
              'You grant us a license to use, modify, and distribute your content',
              'You warrant that your content doesn\'t infringe on others\' rights'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="7. Payment Terms">
          <Typography variant="h6" gutterBottom>7.1 In-App Purchases</Typography>
          <List>
            {[
              'All purchases are final and non-refundable',
              'Prices are subject to change without notice',
              'You are responsible for all charges incurred under your account'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h6" gutterBottom>7.2 Subscription Terms (if applicable)</Typography>
          <List>
            {[
              'Subscription fees are billed in advance',
              'Subscriptions automatically renew unless cancelled',
              'Cancellation must be done 24 hours before renewal'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="8. Limitation of Liability">
          <Typography variant="h6" gutterBottom>8.1 Disclaimer</Typography>
          <List>
            {[
              'The App is provided "AS IS" without warranties',
              'We do not guarantee uninterrupted or error-free service',
              'We are not liable for any indirect, incidental damages'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h6" gutterBottom>8.2 Indemnification</Typography>
          <Typography paragraph>
            You agree to indemnify and hold us harmless from any claims resulting from:
          </Typography>
          <List>
            {[
              'Your use of the App',
              'Your violation of these Terms',
              'Your violation of any third-party rights'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="9. Changes and Termination">
          <Typography variant="h6" gutterBottom>9.1 Changes to Service</Typography>
          <List>
            {[
              'We reserve the right to modify or discontinue the App',
              'We may update these Terms at any time',
              'Continued use after changes constitutes acceptance'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h6" gutterBottom>9.2 Termination</Typography>
          <List>
            {[
              'We may terminate or suspend your account at any time',
              'You may terminate your account at any time',
              'All provisions survive termination'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="10. Third-Party Services">
          <List>
            {[
              'The App may contain links to third-party websites',
              'We are not responsible for third-party content',
              'Your use of third-party services is at your own risk'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="11. User Data and Privacy">
          <List>
            {[
              'Our Privacy Policy explains how we collect and use your data',
              'By using the App, you agree to our Privacy Policy',
              'We comply with applicable data protection laws'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="12. Dispute Resolution">
          <Typography variant="h6" gutterBottom>12.1 Governing Law</Typography>
          <List>
            {[
              'These Terms are governed by Indian law',
              'Disputes will be resolved in courts of Odisha, India'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h6" gutterBottom>12.2 Dispute Process</Typography>
          <List>
            {[
              'Parties will attempt to resolve disputes informally',
              'Formal proceedings may be initiated after 30 days',
              'Small claims may be resolved through arbitration'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="13. Children's Use">
          <List>
            {[
              'The App is not intended for users under 13 years',
              'Parents/guardians are responsible for supervising children',
              'We do not knowingly collect data from children'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="14. App Store Compliance">
          <List>
            {[
              'You agree to comply with all applicable app store terms',
              'These Terms are between you and us, not the app store',
              'App stores are third-party beneficiaries of these Terms'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="15. Contact Information">
          <Typography paragraph>
            For any questions about these Terms:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Email" 
                secondary="dripswater24@gmail.com" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Address" 
                secondary="Plot No - 952, At- Pathargadia, Chandaka, Khordha, Odisha, 754005, India" 
              />
            </ListItem>
          </List>
        </Section>

        <Section title="16. Severability">
          <Typography paragraph>
            If any provision of these Terms is found unenforceable:
          </Typography>
          <List>
            {[
              'The provision will be modified to be enforceable',
              'Other provisions remain in full effect',
              'The overall agreement remains valid'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="17. Entire Agreement">
          <List>
            {[
              'These Terms constitute the entire agreement',
              'They supersede all prior agreements',
              'No waiver of any term shall be deemed a further waiver'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>
      </Paper>
    </Container>
  );
}

export default TermsConditions;